import React from 'react';
import { BarChart3, Briefcase, Users, Clock, Settings, FileText, X } from 'lucide-react';
import { useSettingsStore } from '../data/settingsStore';
interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isAdmin?: boolean;
}
export function Sidebar({
  isOpen = false,
  onClose,
  isAdmin = true
}: SidebarProps) {
  const {
    users
  } = useSettingsStore();
  const currentUser = users.find(user => user.id === 'user-matthew');
  const displayName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Your account';
  const initials = currentUser ? `${currentUser.firstName.slice(0, 1)}${currentUser.lastName.slice(0, 1)}`.toUpperCase() : 'A';
  const navItems = [{
    name: 'Jobs',
    icon: Briefcase,
    href: '/jobs'
  }, {
    name: 'Clients',
    icon: Users,
    href: '/clients'
  }, ...(isAdmin ? [{
    name: 'Invoices',
    icon: FileText,
    href: '/invoices'
  }] : []), {
    name: 'Time Sheets',
    icon: Clock,
    href: '/timesheets'
  }, ...(isAdmin ? [{
    name: 'Reporting',
    icon: BarChart3,
    href: '/reporting'
  }, {
    name: 'Settings',
    icon: Settings,
    href: '/settings'
  }] : [])];
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  return <>
      {/* Mobile backdrop */}
      {isOpen && <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={onClose} aria-hidden="true" data-id="element-402" data-mp="29n5ffebazbbo" />}

      <aside className={`
          w-[260px] flex-shrink-0 bg-[#0F3D5C] text-white flex flex-col h-screen relative overflow-hidden
          fixed md:relative inset-y-0 left-0 z-50
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `} data-id="element-403" data-mp="20ns40g3mccob">
        {/* Background Image Overlay */}
        <div className="absolute inset-0 opacity-20 pointer-events-none mix-blend-overlay" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop")',
        backgroundSize: 'cover',
        backgroundPosition: 'center bottom'
      }} data-id="element-404" data-mp="2m0im0z39w3om" />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full" data-id="element-405" data-mp="237y7kmobm14c">
          {/* Mobile Close Button */}
          <button onClick={onClose} className="md:hidden absolute top-4 right-4 text-white/80 hover:text-white p-1" aria-label="Close menu" data-id="element-406" data-mp="1yay2adzap2ea">
            <X className="w-5 h-5" data-id="element-407" data-mp="3so7ewen5140r" />
          </button>

          {/* Logo Area */}
          <div className="p-6 flex items-center justify-center" data-id="element-408" data-mp="3omlxqh3km3kr">
            <img src="https://cdn.magicpatterns.com/uploads/x8qyon7Q7FP4Ysx38EhDqB/7b33b6e2-5996-44ed-b635-9846f7d8d920.png" alt="RE Cost Seg" className="h-20 w-auto object-contain" data-id="element-409" data-mp="3ok80c1lf4fa1" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1 mt-2" data-id="element-410" data-mp="1k4wca70o1l3z">
            {navItems.map(({
            name,
            icon: Icon,
            href
          }) => {
            const isActive = currentPath === href;
            return <a key={name} href={href} onClick={onClose} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-white/10 text-white font-medium' : 'text-white/80 hover:text-white hover:bg-white/5'}`} data-id="element-411" data-mp="rha22fwxni85">
                  <Icon className="w-5 h-5" data-id="element-412" data-mp="15d41m8btgv8l" />
                  <span className="text-sm" data-id="element-413" data-mp="2buiruknp6llo">{name}</span>
                </a>;
          })}
          </nav>

          {/* Bottom Section */}
          <div className="p-4" data-id="element-414" data-mp="2gwge7nre9wiu">
            <a href="/account" onClick={onClose} aria-current={currentPath === '/account' ? 'page' : undefined} className={`group flex items-center gap-3 rounded-xl p-3 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${currentPath === '/account' ? 'bg-white/15' : 'bg-white/10 hover:bg-white/15'}`} data-id="element-415" data-mp="2fcoldre5oo9i">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/15 text-sm font-semibold text-white ring-2 ring-white/20" data-id="element-416" data-mp="2kbrtjkpfnalp">
                {currentUser?.profileUrl ? <img src={currentUser.profileUrl} alt="" className="h-full w-full object-cover" data-id="element-417" data-mp="2tczcmvjdo4v7" /> : initials}
              </span>
              <span className="min-w-0 flex-1 overflow-hidden" data-id="element-418" data-mp="3332frok7qz79">
                <span className="block truncate text-sm font-semibold" data-id="element-419" data-mp="333kk78h14jq9">{displayName}</span>
                <span className="block truncate text-xs text-white/60" data-id="element-420" data-mp="3h3licg5wtd21">{currentUser?.email || 'Manage your account'}</span>
              </span>
              <span className="text-xs font-medium text-white/0 transition-colors group-hover:text-white/70 group-focus-visible:text-white/70" data-id="element-421" data-mp="3vpixy4fxfgxz">Edit</span>
            </a>
          </div>
        </div>
      </aside>
    </>;
}